import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import Select from "react-select";
import { debounce } from "lodash";

export default function SSPISchoolMapping() {
    // State management
    const [state, setState] = useState({
        schools: [],
        sspiList: [],
        mappings: [],
        loading: true,
        formSubmitting: false,
        formData: {
            sspiSchoolMappingId: 0,
            sspiMasterId: 0,
            schoolId: 0,
            schoolName: "",
            isActive: true,
        },
        searchQuery: "",
        filterStatus: "all",
        currentPage: 0,
        itemsPerPage: 5,
    });

    // Helper function to update state
    const updateState = (newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            await Promise.all([
                fetchSSPISchoolMappings(),
                fetchSSPIList(),
                fetchSchools(),
            ]);
        } catch (error) {
            console.error("Error fetching initial data:", error);
            Swal.fire("Error!", "Failed to load initial data.", "error");
        }
    };

    // API calls
    const fetchSSPIList = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}Sspi/SspiMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                sspiList: Array.isArray(response.data) ? response.data : [],
            });
        } catch (error) {
            console.error("Error fetching SSPI list:", error);
            throw error;
        }
    };

    const fetchSchools = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}School/SchoolMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                schools: Array.isArray(response.data) ? response.data : [],
            });
        } catch (error) {
            console.error("Error fetching schools:", error);
            throw error;
        }
    };

    const fetchSSPISchoolMappings = async () => {
        updateState({ loading: true });
        try {
            const response = await axios.get(
                `${API_LoginURL}Sspi/SspiSchoolMappingList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                mappings: Array.isArray(response.data) ? response.data : [],
                loading: false,
            });
        } catch (error) {
            console.error("Error fetching SSPI-School mappings:", error);
            Swal.fire(
                "Error!",
                "Failed to load SSPI-School mappings.",
                "error"
            );
            
            throw error;
        }
        updateState({ loading: false });
    };

    // Helper function for headers
    const getHeaders = () => ({
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateState({
            formData: {
                ...state.formData,
                [name]: value,
            },
        });
    };

    const handleSSPIChange = (selectedOption) => {
        updateState({
            formData: {
                ...state.formData,
                sspiMasterId: selectedOption ? selectedOption.value : 0,
            },
        });
    };

    const handleSchoolChange = (selectedOption) => {
        updateState({
            formData: {
                ...state.formData,
                schoolId: selectedOption ? selectedOption.value : 0,
                schoolName: selectedOption ? selectedOption.label : "",
            },
        });
    };

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                updateState({ searchQuery: query, currentPage: 0 });
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleFilterChange = (e) => {
        updateState({
            filterStatus: e.target.value,
            currentPage: 0,
        });
    };

    const resetForm = () => {
        updateState({
            formData: {
                sspiSchoolMappingId: 0,
                sspiMasterId: 0,
                schoolId: 0,
                schoolName: "",
                isActive: true,
            },
        });
    };

    const handleEdit = (mapping) => {
        updateState({
            formData: {
                sspiSchoolMappingId: mapping.sspiSchoolMappingId,
                sspiMasterId: mapping.sspiMasterId || 0,
                schoolId: mapping.schoolId || 0,
                schoolName: mapping.schoolName || "",
                isActive: mapping.isActive !== false,
            },
        });
    };

    const handleDelete = async (sspiSchoolMappingId) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this mapping? This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (!confirmation.isConfirmed) return;

        try {
            await axios.post(
                `${API_LoginURL}Sspi/SspiSchoolMappingDelete`,
                { sspiSchoolMappingId },
                { headers: getHeaders() }
            );

            // Update local state
            updateState({
                mappings: state.mappings.filter(
                    (item) => item.sspiSchoolMappingId !== sspiSchoolMappingId
                ),
            });

            Swal.fire("Success!", "Mapping has been deleted.", "success");
        } catch (error) {
            console.error("Error deleting mapping:", error);
            Swal.fire("Error", "Failed to delete mapping", "error");
        }
    };

    const handleStatusChange = async (sspiSchoolMappingId, currentStatus) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: `Do you want to ${
                currentStatus ? "deactivate" : "activate"
            } this mapping?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `Yes, ${
                currentStatus ? "deactivate" : "activate"
            } it!`,
        });

        if (!confirmation.isConfirmed) return;

        try {
            const payload = {
                sspiSchoolMappingId,
                sspiMasterId: state.mappings.find(
                    (m) => m.sspiSchoolMappingId === sspiSchoolMappingId
                )?.sspiMasterId || 0,
                schoolId: state.mappings.find(
                    (m) => m.sspiSchoolMappingId === sspiSchoolMappingId
                )?.schoolId || 0,
                isActive: !currentStatus,
            };

            await axios.post(
                `${API_LoginURL}Sspi/AddEditSspiSchoolMapping`,
                payload,
                { headers: getHeaders() }
            );

            // Update local state
            updateState({
                mappings: state.mappings.map((item) =>
                    item.sspiSchoolMappingId === sspiSchoolMappingId
                        ? { ...item, isActive: !currentStatus }
                        : item
                ),
            });

            Swal.fire(
                "Success!",
                `Mapping has been ${
                    currentStatus ? "deactivated" : "activated"
                }.`,
                "success"
            );
        } catch (error) {
            console.error("Error changing mapping status:", error);
            Swal.fire("Error", "Failed to change mapping status", "error");
        }
    };

    const handlePageChange = ({ selected }) => {
        updateState({ currentPage: selected });
    };

    const validateForm = () => {
        if (!state.formData.sspiMasterId) {
            Swal.fire("Warning", "Please select an SSPI", "warning");
            return false;
        }
        if (!state.formData.schoolId) {
            Swal.fire("Warning", "Please select a school", "warning");
            return false;
        }
        return true;
    };

    const handleFormSubmit = async () => {
        if (!validateForm()) return;

        updateState({ formSubmitting: true });
        try {
            const payload = {
                sspiSchoolMappingId: state.formData.sspiSchoolMappingId,
                sspiMasterId: state.formData.sspiMasterId,
                schoolId: state.formData.schoolId,
                isActive: state.formData.isActive,
            };

            const url = `${API_LoginURL}Sspi/AddEditSspiSchoolMapping`;
            const response = await axios.post(url, payload, {
                headers: getHeaders(),
            });

            if (response.data.response === "SUCCESS") {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text:
                        state.formData.sspiSchoolMappingId > 0
                            ? "Mapping updated successfully"
                            : "Mapping added successfully",
                });
                await fetchSSPISchoolMappings();
                document.getElementById("closeMappingModal").click();
                resetForm();
            } else {
                throw new Error(
                    response.data.message || "Failed to save mapping"
                );
            }
        } catch (error) {
            console.error("Error saving mapping:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    error.message ||
                    "Failed to save mapping",
            });
        } finally {
            updateState({ formSubmitting: false });
        }
    };

    // Filter and pagination
    const filteredMappings = state.mappings.filter((item) => {
        const matchesSearch =
            item.schoolName
                ?.toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            state.sspiList
                .find((sspi) => sspi.sspiMasterId === item.sspiMasterId)
                ?.sspiName?.toLowerCase()
                .includes(state.searchQuery.toLowerCase());

        const matchesStatus =
            state.filterStatus === "all" ||
            (state.filterStatus === "active" && item.isActive) ||
            (state.filterStatus === "inactive" && !item.isActive);

        return matchesSearch && matchesStatus;
    });

    const pageCount = Math.ceil(filteredMappings.length / state.itemsPerPage);
    const displayedMappings = filteredMappings.slice(
        state.currentPage * state.itemsPerPage,
        (state.currentPage + 1) * state.itemsPerPage
    );

    // Prepare select options
    const sspiOptions = state.sspiList.map((sspi) => ({
        value: sspi.sspiMasterId,
        label: sspi.sspiName || `SSPI ${sspi.sspiMasterId}`,
    }));

    const schoolOptions = state.schools.map((school) => ({
        value: school.school_id,
        label: school.school_name,
    }));

    // Get current selected values for the form
    const selectedSSPI = sspiOptions.find(
        (option) => option.value === state.formData.sspiMasterId
    );
    const selectedSchool = schoolOptions.find(
        (option) => option.value === state.formData.schoolId
    );

    // Get SSPI name for display
    const getSSPIName = (sspiMasterId) => {
        const sspi = state.sspiList.find((s) => s.sspiMasterId === sspiMasterId);
        return sspi ? sspi.sspiMasterId : `SSPI ${sspiMasterId}`;
    };

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SASidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    {/* Header Section */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-3 mb-md-0">
                                            SSPI-School Mapping
                                        </h4>

                                        {/* Search and Filter */}
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                                            {/* Search */}
                                            <div
                                                className="flex-grow-1 me-md-2 position-relative"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-5 rounded-pill border-0"
                                                    placeholder="Search by SSPI or School"
                                                    onChange={
                                                        handleSearchChange
                                                    }
                                                    style={{
                                                        height: "38px",
                                                        boxShadow:
                                                            "0 2px 8px rgba(0,0,0,0.08)",
                                                        transition:
                                                            "all 0.3s ease",
                                                        backgroundColor:
                                                            "#f8f9fa",
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 12px rgba(0,0,0,0.15)";
                                                        e.target.style.backgroundColor =
                                                            "#fff";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 8px rgba(0,0,0,0.08)";
                                                        e.target.style.backgroundColor =
                                                            "#f8f9fa";
                                                    }}
                                                />
                                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            </div>

                                            {/* Filter */}
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-filter me-2 text-muted"></i>
                                                <select
                                                    className="form-select"
                                                    value={state.filterStatus}
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                    style={{
                                                        minWidth: "120px",
                                                    }}
                                                >
                                                    <option value="all">
                                                        All Status
                                                    </option>
                                                    <option value="inactive">
                                                        Active
                                                    </option>
                                                    <option value="active">
                                                        Inactive
                                                    </option>
                                                </select>
                                            </div>

                                            {/* Add Mapping Button */}
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                data-bs-toggle="modal"
                                                data-bs-target="#mappingModal"
                                                onClick={resetForm}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                <span className="d-none d-md-inline">
                                                    Add Mapping
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mappings Table */}
                                    <div className="table-responsive">
                                        {state.loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ height: "200px" }}
                                            >
                                                <RotatingLines
                                                    color="#4e73df"
                                                    size={50}
                                                />
                                                <p className="mt-3 ms-3">
                                                    Loading mappings...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="table table-hover table-striped">
                                                    <thead className="table-primary">
                                                        <tr>
                                                            <th width="5%">
                                                                ID
                                                            </th>
                                                            <th width="30%">
                                                                SSPI
                                                            </th>
                                                            <th width="30%">
                                                                School
                                                            </th>
                                                            <th width="10%">
                                                                Status
                                                            </th>
                                                            <th
                                                                width="25%"
                                                                className="text-end"
                                                            >
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedMappings.length >
                                                        0 ? (
                                                            displayedMappings.map(
                                                                (item) => (
                                                                    <tr
                                                                        key={
                                                                            item.sspiSchoolMappingId
                                                                        }
                                                                    >
                                                                        <td>
                                                                            {
                                                                                item.sspiSchoolMappingId
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {getSSPIName(
                                                                                item.sspiMasterId
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.schoolName
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                color: item.isActive
                                                                                    ? "red"
                                                                                    : "green",
                                                                            }}
                                                                        >
                                                                            {item.isActive
                                                                                ? "Inactive"
                                                                                : "Active"}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-2">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#mappingModal"
                                                                                    onClick={() =>
                                                                                        handleEdit(
                                                                                            item
                                                                                        )
                                                                                    }
                                                                                    title="Edit"
                                                                                >
                                                                                    <i className="fas fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    className={`btn btn-sm ${
                                                                                        item.isActive
                                                                                            ? "btn-outline-success"
                                                                                            : "btn-outline-danger"
                                                                                    }`}
                                                                                    onClick={() =>
                                                                                        handleStatusChange(
                                                                                            item.sspiSchoolMappingId,
                                                                                            item.isActive
                                                                                        )
                                                                                    }
                                                                                    title={
                                                                                        item.isActive
                                                                                            ? "Activate"
                                                                                            : "Deactivate"
                                                                                    }
                                                                                >
                                                                                    <i
                                                                                        className={`fas ${
                                                                                            item.isActive
                                                                                                ? "fa-check"
                                                                                                : "fa-times"
                                                                                        }`}
                                                                                    ></i>
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() =>
                                                                                        handleDelete(
                                                                                            item.sspiSchoolMappingId
                                                                                        )
                                                                                    }
                                                                                    title="Delete"
                                                                                >
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    colSpan="5"
                                                                    className="text-center py-4"
                                                                >
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <img
                                                                            src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                                                            alt="No mappings"
                                                                            style={{
                                                                                opacity: 0.5,
                                                                                width: "60px",
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 text-muted">
                                                                            No
                                                                            mappings
                                                                            found
                                                                        </p>
                                                                        <button
                                                                            className="btn btn-sm btn-primary mt-2"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#mappingModal"
                                                                            onClick={
                                                                                resetForm
                                                                            }
                                                                        >
                                                                            Add
                                                                            New
                                                                            Mapping
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {displayedMappings.length >
                                                    0 && (
                                                    <ReactPaginate
                                                        previousLabel={
                                                            <i className="fas fa-chevron-left"></i>
                                                        }
                                                        nextLabel={
                                                            <i className="fas fa-chevron-right"></i>
                                                        }
                                                        pageCount={pageCount}
                                                        onPageChange={
                                                            handlePageChange
                                                        }
                                                        containerClassName={
                                                            "pagination justify-content-center mt-4"
                                                        }
                                                        pageClassName={
                                                            "page-item"
                                                        }
                                                        pageLinkClassName={
                                                            "page-link"
                                                        }
                                                        previousClassName={
                                                            "page-item"
                                                        }
                                                        previousLinkClassName={
                                                            "page-link"
                                                        }
                                                        nextClassName={
                                                            "page-item"
                                                        }
                                                        nextLinkClassName={
                                                            "page-link"
                                                        }
                                                        activeClassName={
                                                            "active"
                                                        }
                                                        breakLabel={"..."}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        forcePage={
                                                            state.currentPage
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mapping Modal */}
            <div
                className="modal fade"
                id="mappingModal"
                tabIndex="-1"
                aria-labelledby="mappingModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="mappingModalLabel">
                                {state.formData.sspiSchoolMappingId > 0
                                    ? "Edit Mapping"
                                    : "Add New Mapping"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="mb-3">
                                    <label
                                        htmlFor="sspiMasterId"
                                        className="form-label required"
                                    >
                                        SSPI
                                    </label>
                                    <Select
                                        id="sspiMasterId"
                                        options={sspiOptions}
                                        value={selectedSSPI}
                                        onChange={handleSSPIChange}
                                        placeholder="Select SSPI"
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable
                                        isDisabled={
                                            state.formData.sspiSchoolMappingId > 0
                                                ? true
                                                : false
                                        }
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: "40px",
                                            }),
                                        }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label
                                        htmlFor="schoolId"
                                        className="form-label required"
                                    >
                                        School
                                    </label>
                                    <Select
                                        id="schoolId"
                                        options={schoolOptions}
                                        value={selectedSchool}
                                        onChange={handleSchoolChange}
                                        placeholder="Select School"
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: "40px",
                                            }),
                                        }}
                                    />
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                id="closeMappingModal"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                                disabled={state.formSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleFormSubmit}
                                disabled={state.formSubmitting}
                            >
                                {state.formSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Submitting...
                                    </>
                                ) : state.formData.sspiSchoolMappingId > 0 ? (
                                    "Update Mapping"
                                ) : (
                                    "Add Mapping"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}